import { Box, Toolbar } from '@mui/material';
import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setMobileSidebarOpen } from '@/app/uiSlice';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { PageSkeleton } from '@/components/feedback/Skeletons';
import { BusinessNameDialog } from '@/features/account/BusinessNameDialog';

import { Footer } from './Footer';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

/**
 * Application chrome: fixed header at 64 px, persistent dark sidebar, routed content, footer.
 *
 * The ErrorBoundary wraps the outlet only — a broken page leaves navigation intact.
 */
export function AppLayout() {
  const dispatch = useAppDispatch();
  const sidebarCollapsed = useAppSelector((state) => state.ui.sidebarCollapsed);
  const mobileSidebarOpen = useAppSelector((state) => state.ui.mobileSidebarOpen);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      <Sidebar
        collapsed={sidebarCollapsed}
        mobileSidebarOpen={mobileSidebarOpen}
        onMobileClose={() => dispatch(setMobileSidebarOpen(false))}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: '100vh',
        }}
      >
        {/* Offset the fixed AppBar */}
        <Toolbar sx={{ minHeight: '64px !important' }} />

        <Box
          sx={{
            flex: 1,
            p: { xs: '18px 16px 34px', md: '22px 26px 40px' },
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ErrorBoundary>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Box>

        <Footer />
        <BusinessNameDialog />
      </Box>
    </Box>
  );
}
